import React, { useState } from 'react';
import { Btn, Card, SectionTitle, ValidationInput, ProgressBar } from '../components/UI';
import { validateAmount, validateTxId } from '../data';

const MONTHS_2025 = ['Jan 2025','Feb 2025','Mar 2025','Apr 2025','May 2025','Jun 2025','Jul 2025','Aug 2025','Sep 2025','Oct 2025','Nov 2025','Dec 2025'];
const PAID_MONTHS = ['Jan 2025','Feb 2025','Mar 2025','Apr 2025','May 2025'];
const DUE = 16000;

export default function PaymentScreen({ t, onSubmit }) {
  const [method, setMethod] = useState('bKash');
  const [amount, setAmount] = useState('');
  const [txId, setTxId] = useState('');
  const [screenshot, setScreenshot] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState(['Jun 2025', 'Jul 2025']);
  const [payType, setPayType] = useState('monthly');
  const [submitted, setSubmitted] = useState(false);

  const amtResult = amount ? validateAmount(amount, DUE) : null;
  const txResult = method !== 'Cash' ? (txId ? validateTxId(method, txId) : null) : { ok: true, msg: '✓ Cash — no transaction ID needed' };
  const monthsOk = selectedMonths.length > 0;
  const checks = [amtResult?.ok, txResult?.ok, screenshot, monthsOk].filter(Boolean).length;
  const allValid = checks === 4;

  const handleSuggest = () => setAmount('16,000');

  const toggleMonth = (m) => {
    if (PAID_MONTHS.includes(m)) return;
    setSelectedMonths(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  };

  if (submitted) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
      <div style={{ fontSize: 22, fontWeight: 900, color: t.primary, marginBottom: 8 }}>Submitted!</div>
      <div style={{ fontSize: 14, color: t.textSec, fontWeight: 600, lineHeight: 1.6 }}>Your payment has been submitted and is pending admin review.</div>
      <div style={{ background: t.primaryLight, borderRadius: 12, padding: '14px 16px', marginTop: 20, border: `0.5px solid ${t.borderLight}`, width: '100%', maxWidth: 380 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: t.primary, marginBottom: 8 }}>What happens next:</div>
        {['Admin reviews your payment + screenshot', 'On approval — receipt auto-generated', 'PDF receipt emailed to rasel@gmail.com', 'Appears in your My Receipts tab'].map(s => (
          <div key={s} style={{ fontSize: 12, color: t.text, fontWeight: 600, lineHeight: 1.8 }}>✓ {s}</div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '16px 16px 32px' }}>
      {/* Smart suggestion */}
      <div style={{ background: t.isDark ? 'linear-gradient(135deg,#0d1f0d,#1a2e1a)' : 'linear-gradient(135deg,#1B5E20,#2E7D32)', borderRadius: 14, padding: '14px 16px', marginBottom: 16, border: `0.5px solid ${t.borderLight}` }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 20 }}>💡</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#fff', fontSize: 14, fontWeight: 800 }}>Smart suggestion</div>
            <div style={{ color: '#A5D6A7', fontSize: 12, fontWeight: 600, marginTop: 3 }}>2 unpaid months (Jun + Jul 2025) · Suggested: <strong style={{ color: '#FDD835' }}>16,000 BDT</strong></div>
            <div style={{ color: '#A5D6A7', fontSize: 11, fontWeight: 500, marginTop: 2 }}>2 shares × 4,000/share × 2 months</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <div onClick={handleSuggest} style={{ background: '#fff', color: '#1B5E20', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>✓ Apply suggestion</div>
              <div style={{ background: 'rgba(255,255,255,.15)', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', border: '0.5px solid rgba(255,255,255,.3)' }}>Custom amount</div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment type */}
      <Card t={t} style={{ marginBottom: 14 }}>
        <SectionTitle t={t} label="Payment type" />
        <div style={{ display: 'flex', gap: 8 }}>
          {[['monthly', '📅 Monthly install.'], ['dp', '💰 Down payment'], ['both', '📋 Both']].map(([k, l]) => (
            <div key={k} onClick={() => setPayType(k)} style={{ flex: 1, padding: '10px 6px', borderRadius: 9, textAlign: 'center', cursor: 'pointer', background: payType === k ? t.primaryLight : t.surface2, border: `${payType === k ? 1.5 : 0.5}px solid ${payType === k ? t.primary : t.border}`, fontSize: 11, fontWeight: payType === k ? 800 : 600, color: payType === k ? t.primary : t.textSec }}>{l}</div>
          ))}
        </div>
      </Card>

      {/* Month selection */}
      <Card t={t} style={{ marginBottom: 14 }}>
        <SectionTitle t={t} label="Select months — P02" right={<span style={{ fontSize: 11, color: t.textHint, fontWeight: 600 }}>{selectedMonths.length} selected</span>} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
          {MONTHS_2025.map(m => {
            const isPaid = PAID_MONTHS.includes(m);
            const isSelected = selectedMonths.includes(m);
            return (
              <div key={m} onClick={() => toggleMonth(m)} style={{ padding: '7px 4px', borderRadius: 8, textAlign: 'center', cursor: isPaid ? 'not-allowed' : 'pointer', background: isPaid ? t.primaryLight : isSelected ? t.primary2 : t.surface2, border: `0.5px solid ${isPaid ? t.borderLight : isSelected ? t.primary : t.border}`, fontSize: 11, fontWeight: 700, color: isPaid ? t.primary : isSelected ? '#fff' : t.textSec }}>
                <div>{m.split(' ')[0]}</div>
                {isPaid && <div style={{ fontSize: 9, marginTop: 1 }}>✓ Paid</div>}
              </div>
            );
          })}
        </div>
        {selectedMonths.length > 0 && (
          <div style={{ background: t.primaryLight, borderRadius: 8, padding: '8px 10px', marginTop: 10, border: `0.5px solid ${t.borderLight}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: t.textSec, fontWeight: 600 }}>{selectedMonths.join(' + ')} · 2 shares</span>
            <strong style={{ fontSize: 14, color: t.primary }}>Due: {(selectedMonths.length * 4000 * 2).toLocaleString()}</strong>
          </div>
        )}
      </Card>

      {/* Payment method */}
      <Card t={t} style={{ marginBottom: 14 }}>
        <SectionTitle t={t} label="Payment method" />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {['bKash', 'Nagad', 'Bank', 'Cash'].map(m => (
            <div key={m} onClick={() => { setMethod(m); setTxId(''); }} style={{ padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: method === m ? t.primary2 : t.surface2, color: method === m ? '#fff' : t.textSec, border: `0.5px solid ${method === m ? t.primary : t.border}`, cursor: 'pointer' }}>{m}</div>
          ))}
        </div>

        {/* Amount validation */}
        <ValidationInput t={t} label="Amount (BDT)" value={amount} onChange={setAmount} placeholder={`Minimum: ${DUE.toLocaleString()}`} result={amtResult} hint={`Due: ${DUE.toLocaleString()} (2sh × 4,000 × ${selectedMonths.length}mo)`} />

        {/* Tx ID validation */}
        {method !== 'Cash' && (
          <ValidationInput t={t} label="Transaction ID" value={txId} onChange={setTxId} placeholder={method === 'bKash' ? 'BK + 10 digits (e.g. BK2507161234)' : method === 'Nagad' ? 'NG + 10 digits' : '8-20 alphanumeric chars'} result={txResult} hint={method === 'bKash' ? 'Format: BK followed by exactly 10 digits' : method === 'Nagad' ? 'Format: NG followed by exactly 10 digits' : '8-20 alphanumeric characters'} />
        )}

        {/* Screenshot */}
        <div style={{ fontSize: 12, color: t.isDark ? t.primary : '#555', fontWeight: 700, marginBottom: 6 }}>Screenshot <span style={{ color: t.red }}>*</span></div>
        <div onClick={() => setScreenshot(s => !s)} style={{ border: `1.5px dashed ${screenshot ? t.primary : t.borderLight}`, borderRadius: 10, padding: '16px 0', textAlign: 'center', background: screenshot ? t.primaryLight : t.surface2, cursor: 'pointer' }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>{screenshot ? '✅' : '📷'}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: screenshot ? t.primary : t.textSec }}>{screenshot ? 'Screenshot attached' : 'Tap to attach payment screenshot'}</div>
          <div style={{ fontSize: 11, color: t.textHint, marginTop: 3, fontWeight: 500 }}>JPG · PNG · WebP · max 2MB</div>
        </div>
      </Card>

      {/* Validation progress */}
      <Card t={t} style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
          <span style={{ color: t.primary }}>Validation</span>
          <span style={{ color: allValid ? t.primary : t.pendingText }}>{checks} / 4 checks passed</span>
        </div>
        <ProgressBar t={t} value={checks} max={4} color={allValid ? t.primary : t.pendingText} height={8} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginTop: 10 }}>
          {[['Months selected', monthsOk], ['Amount valid', amtResult?.ok], ['Tx ID valid', txResult?.ok], ['Screenshot attached', screenshot]].map(([l, ok]) => (
            <div key={l} style={{ fontSize: 11, color: ok ? t.primary : t.textHint, fontWeight: ok ? 700 : 500, display: 'flex', alignItems: 'center', gap: 5 }}>
              <span>{ok ? '✅' : '○'}</span>{l}
            </div>
          ))}
        </div>
      </Card>

      {/* Auto-email notice */}
      <div style={{ background: t.primaryLight, borderRadius: 10, padding: '10px 14px', marginBottom: 16, border: `0.5px solid ${t.borderLight}`, fontSize: 12, color: t.primary, fontWeight: 600, lineHeight: 1.6 }}>
        💡 On admin approval, receipt PDF will be auto-generated and emailed to rasel@gmail.com. No extra steps needed.
      </div>

      <Btn t={t} label={allValid ? '✓ Submit payment' : 'Fix errors above to submit'} onClick={allValid ? () => { setSubmitted(true); setTimeout(() => { onSubmit(); setSubmitted(false); }, 2000); } : null} disabled={!allValid} />
    </div>
  );
}
